import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Table from "@/components/Table/table";
import { useColumnDefs } from "@/features/claimsummary/summary/constant/claimFormHeaderConst";
import { MotifButton, MotifProgressLoader } from "@ey-xd/motif-react";
import {
  useGetclaimStageAndDocumentApiDataQuery,
  useGetDocumentViewDataQuery,
} from "@/services/claimSummary/caseSummaryReducer";
import UploadEditDocument from "./uploadEditDocument/uploadEditDocument";
import { useCase } from "@/features/case/use-case";
import {
  useLazyDownloadDocumentQuery,
  useDeleteDocumentMutation,
} from "@/services/documents/documentsReducer";
import DeleteModal from "@/components/Modal/DeleteModal/DeleteModal";
import { useSelector } from "react-redux";
import {
  havingAddPermission,
  havingDeletePermission,
  havingEditPermission,
} from "@/utility/common";
import {
  GetCommonDeleteImgButton,
  GetCommonDownloadImgButton,
} from "@/components/Images/CommonImages";
import NotificationGrid from "@/components/Notification/NotificationGrid";
export const Documents = () => {
  const parameter = useParams();
  const { t } = useTranslation();
  const caseData = useCase();
  const { caseId } = caseData;
  // @ts-ignore
  const getDataByUrl = { caseId: caseId, claimId: parameter?.claimId };
  const { data, isLoading, refetch } =
    useGetDocumentViewDataQuery(getDataByUrl);
  const { refetch: reftechClaimStageData } =
    useGetclaimStageAndDocumentApiDataQuery(parameter?.claimId);
  const [showUploadEdit, setShowUploadEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedDocument, setSelectedDocuement] = useState<any>({});
  const [downloadDocument, { isLoading: downloadLoading }] =
    useLazyDownloadDocumentQuery();
  const [deleteDocument, { isLoading: deleteLoading }] =
    useDeleteDocumentMutation();
  const permissions = useSelector((state: any) => state.permissions);
  const { Document } = permissions;
  const columnDefs = useColumnDefs(caseData?.abbreviation);
  const [errorMessage, setErrorMessage] = useState("");
  useEffect(() => {
    if (showUploadEdit === false) {
      refetch();
    }
    // eslint-disable-next-line
  }, [showUploadEdit]);

  function TitleComplement() {
    return (
      <>
        {havingAddPermission(Document) && (
          <MotifButton
            onClick={handleUploadNew}
            size="small"
            type="submit"
            variant="secondary"
          >
            {t("pages.claims.claimsDoumentsTab.uploadButton")}
          </MotifButton>
        )}
      </>
    );
  }

  const customActionHeader = {
    nameCellRenderer: function (props: any) {
      return (
        <div
          className={
            havingEditPermission(Document) ? `text-bold-underline` : ""
          }
          style={
            havingEditPermission(Document)
              ? { cursor: "pointer" }
              : { cursor: "default" }
          }
          onClick={() => {
            if (havingEditPermission(Document)) {
              handleEdit(props.data);
            }
          }}
        >
          {props.data.displayName}
        </div>
      );
    },
    downloadActionRenderer: function (props: any) {
      return (
        <>
          <GetCommonDownloadImgButton
            onClick={() => handleDownload(props.data)}
            loading={downloadLoading}
          ></GetCommonDownloadImgButton>
        </>
      );
    },
    actionRenderer: function (props: any) {
      return (
        <>
          {havingDeletePermission(Document) && (
            <GetCommonDeleteImgButton
              onClick={() => {
                setSelectedDocuement(props.data);
                setShowDelete(true);
              }}
            ></GetCommonDeleteImgButton>
          )}
        </>
      );
    },
  };

  const handleUploadNew = () => {
    setIsEdit(false);
    setSelectedDocuement({});
    setShowUploadEdit(true);
  };

  const handleEdit = (documentData: any) => {
    setIsEdit(true);
    setSelectedDocuement(documentData);
    setShowUploadEdit(true);
  };

  const handleDownload = (documentData: any) => {
    setErrorMessage("");
    downloadDocument({
      caseId: caseId,
      documentId: documentData.id,
      fileName: documentData.fileName,
    })
      .then((response) => {
        if (response?.error && "data" in response.error) {
          try {
            const errObj = JSON.parse(response?.error?.data as string);
            setErrorMessage(errObj?.message || errObj.title);
          } catch (error) {
            setErrorMessage("Something went wrong!!");
            console.error("File download error:", error);
          }
        }
      })
      .catch((error: any) => {
        setErrorMessage("Something went wrong!!");
        console.error("File download error:", error);
      });
  };

  const handleDelete = () => {
    deleteDocument({
      CaseId: caseId,
      DocumentId: selectedDocument.id,
    }).then(() => {
      setShowDelete(false);
      refetch();
      reftechClaimStageData();
    });
  };

  return (
    <>
      <div className="mar-b-20">
        {isLoading ? (
          <MotifProgressLoader show variant="default" className="mar-b-20" />
        ) : (
          <>
            {errorMessage && (
              <div className="pad-b-10">
                <NotificationGrid
                  show={true}
                  type="error"
                  message={errorMessage}
                  scrollToTop={false}
                />
              </div>
            )}
            <Table
              columnDefs={columnDefs}
              cellRenderers={data}
              TitleComplement={TitleComplement}
              title={"pages.claims.claimsDoumentsTab.tableTitle"}
              description={"pages.claims.claimsDoumentsTab.tableDescription"}
              caseRenderers={customActionHeader}
              pagination={false}
              overlayNoRowsTemplate={t("global.globalNotifications.noRecords")}
            />
          </>
        )}
      </div>

      {showUploadEdit && (
        <UploadEditDocument
          show={showUploadEdit}
          setShow={setShowUploadEdit}
          claimId={parameter?.claimId!}
          isEdit={isEdit}
          document={selectedDocument}
        />
      )}

      <DeleteModal
        message={t("pages.claims.documentPopup.deleteMessage")}
        isVisible={showDelete}
        setIsVisible={setShowDelete}
        cancelBtnHandler={() => setShowDelete(false)}
        yesBtnHandler={handleDelete}
        deleteLoading={deleteLoading}
        entityName={selectedDocument?.displayName}
      />
    </>
  );
};
